import type { ReactNode } from "react";
import { Container, Grid } from "@mui/material";
import { Authorization } from "@sdeapps/react-core";
import { routesConfig } from "config/app-config";
import { ApplicationRole } from "constants/ApplicationRole";
import AgExportButton from "./components/AgExportButton";
import {
  etatPrefectureCollectivites,
  etatPrefectureCommunesIsolees,
  etatPrefectureConseilGeneral,
} from "constants/EtatPrefecture";
import PageTitle from "components/PageTitle";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import WaterDamageOutlinedIcon from "@mui/icons-material/WaterDamageOutlined";
import { AdministrationArea } from "./components/AdministrationArea";
import EtatPrefectureButton from "./components/EtatPrefectureButton";
import { NavigationButton } from "./components/NavigationButton";
import { CommuneIcon, SdeaIcon } from "icons";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import ClearIcon from "@mui/icons-material/Clear";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

function AdministrationPage(): ReactNode {
  return (
    <Authorization roles={[ApplicationRole.ADMINISTRATOR, ApplicationRole.GESTIONNAIRE_ELECTIONS]}>
      <Container maxWidth={false}>
        <Grid container spacing={2}>
          <PageTitle title="Administration" />

          <Authorization roles={ApplicationRole.ADMINISTRATOR}>
            <AdministrationArea
              title="Exports"
              icon={<AutoStoriesOutlinedIcon />}
              childrenSpacing={0}
              items={[
                <AgExportButton key="agExportButton" />,
                ...[
                  ...etatPrefectureCommunesIsolees,
                  ...etatPrefectureCollectivites,
                  ...etatPrefectureConseilGeneral,
                ].map(({ type, libelle }) => (
                  <EtatPrefectureButton key={type} type={type} libelle={libelle} />
                )),
              ]}
            />
          </Authorization>

          <Authorization roles={ApplicationRole.ADMINISTRATOR}>
            <AdministrationArea
              title="Périmètres"
              icon={<SdeaIcon />}
              items={[
                <NavigationButton
                  key="nouveau-perimetre"
                  libelle="Nouveau Périmètre"
                  href={routesConfig.newPerimetre.path}
                />,
                <NavigationButton
                  key="fusion-perimetres"
                  icon={<MergeTypeIcon />}
                  libelle="Fusionner deux Périmètres"
                  href={routesConfig.fusionPerimetres.path}
                  color="secondary"
                />,
                <NavigationButton
                  key="dissoudre-perimetre"
                  icon={<ClearIcon />}
                  libelle="Dissoudre un Périmètre"
                  href={routesConfig.dissoudrePerimetre.path}
                  color="secondary"
                />,
              ]}
            />
          </Authorization>

          <Authorization roles={ApplicationRole.ADMINISTRATOR}>
            <AdministrationArea
              title="Transferts"
              icon={<AddBusinessOutlinedIcon />}
              items={[
                <NavigationButton
                  key="nouveau-transfert"
                  libelle="Nouveaux Transferts"
                  href={routesConfig.newTransferts.path}
                />,
              ]}
            />
          </Authorization>

          <Authorization roles={ApplicationRole.ADMINISTRATOR}>
            <AdministrationArea
              title="Communes"
              icon={<CommuneIcon />}
              items={[
                <Authorization
                  key="affectation-antenne-communes"
                  roles={ApplicationRole.ADMINISTRATOR}>
                  <NavigationButton
                    icon={<WaterDamageOutlinedIcon />}
                    libelle="Affectation d'antenne"
                    href={routesConfig.antennesAffectationCommunes.path}
                  />
                </Authorization>,
              ]}
            />
          </Authorization>

          <AdministrationArea
            title="Personnes"
            icon={<GroupsIcon />}
            items={[
              <Authorization
                key="nouvelle-personne"
                roles={[ApplicationRole.ADMINISTRATOR, ApplicationRole.GESTIONNAIRE_ELECTIONS]}>
                <NavigationButton
                  icon={<PersonAddAlt1Icon />}
                  libelle="Nouvelle Personne"
                  href={routesConfig.newPersonne.path}
                />
              </Authorization>,
              <Authorization key="fusion-personnes" roles={ApplicationRole.ADMINISTRATOR}>
                <NavigationButton
                  icon={<MergeTypeIcon />}
                  libelle="Fusionner deux Personnes"
                  href={routesConfig.fusionPersonnes.path}
                  color="secondary"
                />
              </Authorization>,
            ]}
          />
        </Grid>
      </Container>
    </Authorization>
  );
}

export default AdministrationPage;
